import React, {useEffect, useMemo, useRef, useState} from "react";
import {KatButton, KatInput, KatModal, KatSpinner} from "@amzn/katal-react";
import {OdqAndReliabilityApi} from "src/app/api/OdqAndReliabilityApi";
import {getCategoryLabel, getReasonCodeOption, isMappedReasonCode} from "src/app/util/ExemptionMappingUtil";
import CustomDropdown from "src/app/component/common/CustomDropdown";

interface ExemptionModalProps {
    visible: boolean;
    transporterId: string;
    targetIds: string[];
    selectedTrackingIds: string[];
    country: string;
    opportunityType: string;
    onSubmit: (category: string, reasonCode: string, ticketId: string) => void;
    onClose: () => void;
}

interface ReasonCode {
    reasonCodeKey: string;
    title: string;
}

interface CategoryWithReasonCodes {
    category: { categoryKey: string; title: string };
    reasonCodes: ReasonCode[];
}


/**
 * ExemptionModal - Shows Category + Reason Code dropdowns + Ticket ID input.
 * Category and Reason Code options fetched from GET /odq/reason-codes.
 * Reason codes filtered by selected category.
 */
const ExemptionModal: React.FC<ExemptionModalProps> = ({visible, transporterId, targetIds, selectedTrackingIds, country, opportunityType, onSubmit, onClose}) => {
    const [category, setCategory] = useState("");
    const [reasonCode, setReasonCode] = useState("");
    const [ticketId, setTicketId] = useState("");
    const [mappings, setMappings] = useState<CategoryWithReasonCodes[]>([]);
    const [loadingCodes, setLoadingCodes] = useState(false);
    const ticketRef = useRef<any>(null);

    useEffect(() => {
        if (mappings.length === 0) {
            loadReasonCodes();
        }
    }, []);

    const loadReasonCodes = async () => {
        setLoadingCodes(true);
        try {
            const response = await OdqAndReliabilityApi.getCategoryAndReasonCodeMappings(country, opportunityType);
            setMappings(response.data?.mappings || []);
        } catch (error) {
            console.error('[ExemptionModal] Failed to load reason codes', error);
        } finally {
            setLoadingCodes(false);
        }
    };

    const filteredReasonCodes = useMemo(() => {
        if (!category) return [];
        const mapping = mappings.find(m => m.category.categoryKey === category);
        return mapping ? mapping.reasonCodes : [];
    }, [category, mappings]);

    const categoryOptions = mappings
        .filter(m => m.category?.categoryKey && getCategoryLabel(m.category.categoryKey))
        .map(m => ({value: m.category.categoryKey, title: getCategoryLabel(m.category.categoryKey)!}));

    // Offer only codes the UI has a curated entry for, tested by an explicit lookup rather than by
    // comparing the resolved label against the key. The previous label-vs-key check also discarded
    // codes that ARE mapped but whose label legitimately equals the key, which is what hid LSE from
    // NO_SHOW_LATE_CANCEL even though both the API and the static map carried it.
    const reasonCodeOptions = filteredReasonCodes
        .filter(rc => rc?.reasonCodeKey && isMappedReasonCode(rc.reasonCodeKey, category))
        .map(rc => {
            const mapped = getReasonCodeOption(rc.reasonCodeKey, category);
            return {value: rc.reasonCodeKey, title: mapped.label, description: mapped.description};
        });

    const selectedReasonDescription = useMemo(() => {
        if (!reasonCode || !category) return undefined;
        const mapped = getReasonCodeOption(reasonCode, category);
        return mapped.description;
    }, [reasonCode, category]);


    const handleCategoryChange = (value: string) => {
        setCategory(value);
        setReasonCode("");
    };

    const handleTicketChange = () => {
        setTicketId(ticketRef.current?.value || "");
    };

    const handleSubmit = () => {
        onSubmit(category, reasonCode, ticketId.trim());
        resetForm();
    };

    const handleClose = () => {
        onClose();
        resetForm();
    };

    const resetForm = () => {
        setCategory("");
        setReasonCode("");
        setTicketId("");
    };

    return (
        <KatModal visible={visible} onClose={handleClose}>
            <span slot="title">Exemption Request</span>
            <div className="odq-exempt-modal">
                <div className="odq-exempt-modal__header">
                    <strong>Transporter ID:</strong> {transporterId}
                </div>

                <div className="odq-exempt-modal__selected">
                    <strong>Selected Items:</strong> {targetIds.length} defect{targetIds.length > 1 ? "s" : ""} selected
                    {selectedTrackingIds.some(id => id !== "N/A") && (
                        <div className="odq-exempt-modal__id-list">
                            {selectedTrackingIds.filter(id => id !== "N/A").map((id) => (
                                <span key={id} className="odq-exempt-modal__id-chip">{id}</span>
                            ))}
                        </div>
                    )}
                </div>

                {loadingCodes ? (
                    <KatSpinner size="small" />
                ) : mappings.length === 0 ? (
                    <p className="odq-exempt-modal__empty" data-testid="no-reason-codes">
                        Reason codes not available. API integration pending.
                    </p>
                ) : (
                    <>
                        <div className="odq-exempt-modal__field">
                            <CustomDropdown
                                label="Category"
                                placeholder="--Select Category--"
                                options={categoryOptions}
                                value={category}
                                onChange={handleCategoryChange}
                                testId="category-select"
                            />
                        </div>

                        <div className="odq-exempt-modal__field">
                            <CustomDropdown
                                label="Reason Code"
                                placeholder="--Select Reason Code--"
                                options={reasonCodeOptions}
                                value={reasonCode}
                                onChange={setReasonCode}
                                disabled={!category}
                                testId="reason-code-select"
                            />
                            {selectedReasonDescription && (
                                <div className="odq-exempt-modal__definition">
                                    <strong>Definition:</strong> {selectedReasonDescription}
                                </div>
                            )}
                        </div>
                    </>
                )}

                <div className="odq-exempt-modal__field">
                    <KatInput
                        label="Ticket/Contact ID"
                        placeholder="Enter ticket or contact ID"
                        value={ticketId}
                        ref={ticketRef}
                        onChange={handleTicketChange}
                        onKeyup={handleTicketChange}
                        onInput={handleTicketChange}
                        data-testid="ticket-id-input"
                    />
                    <span className="odq-exempt-modal__hint">
                        Provide the support ticket ID or contact reference for this exemption request.
                    </span>
                </div>
            </div>
            <div slot="footer" className="kat-group-horizontal">
                <KatButton variant="secondary" label="Cancel" onClick={handleClose}
                           data-testid="cancel-btn" />
                <KatButton variant="primary" label="Submit Exemption"
                           disabled={!category || !reasonCode || !ticketId.trim()}
                           onClick={handleSubmit}
                           data-testid="submit-btn" />
            </div>
        </KatModal>
    );
};

export default ExemptionModal;
